import { useEffect, useState } from 'react';
import type { DrawingDocument } from '@peruselab/structure-editor/model';
import { toChemMoleculeV1 } from '@peruselab/structure-editor/domain/moleculeProjection';
import { computeMolecularFormula } from '@peruselab/structure-editor/chemistry/moleculeFormula';
import type { SearchResults } from '@peruselab/api-client/moleculeSearch';
import { getMoleculeSummary } from '@peruselab/api-client/molecules';
import { queryMolecules } from '../hooks/queryMolecules';

export interface ExportStemOptions {
  readonly userTitle?: string | null | undefined;
  readonly savedMoleculeName?: string | null | undefined;
  readonly formula?: string | null | undefined;
  readonly isMarkush?: boolean | undefined;
  readonly fallback?: string | undefined;
}

const SUBSCRIPT_TO_DIGIT: Readonly<Record<string, string>> = {
  '₀': '0',
  '₁': '1',
  '₂': '2',
  '₃': '3',
  '₄': '4',
  '₅': '5',
  '₆': '6',
  '₇': '7',
  '₈': '8',
  '₉': '9'
};

/**
 * #1189: Normalize unicode subscript digits to ASCII digits for filenames.
 */
export function normalizeFormulaForFileName(formula: string): string {
  return formula.replace(/[₀-₉]/g, (char) => SUBSCRIPT_TO_DIGIT[char] ?? char);
}

/**
 * #1189: Sanitize a string into a clean, cross-platform file name stem.
 * Preserves unicode letters/numbers, replaces spaces, slashes, middle dots, and forbidden characters
 * with hyphens, collapses consecutive hyphens, and trims leading/trailing punctuation.
 */
export function sanitizeFileStem(name: string): string {
  return name
    .trim()
    .replace(/[₀-₉]/g, (char) => SUBSCRIPT_TO_DIGIT[char] ?? char)
    .replace(/[·•]/g, '-')
    .replace(/[^\p{L}\p{N}._+-]+/gu, '-')
    .replace(/-+/g, '-')
    .replace(/^[._-]+|[._-]+$/g, '');
}

/**
 * #1189: Resolve export file stem according to the priority hierarchy:
 * 1. User-provided title for the drawing.
 * 2. Saved molecule name (from database or PubChem enrichment).
 * 3. Molecular formula.
 * 4. Default fallback ("chem-drawing" or "markush-drawing").
 */
export function resolveExportFileStem(options: ExportStemOptions): string {
  const { userTitle, savedMoleculeName, formula, isMarkush, fallback } = options;

  if (userTitle) {
    const sanitized = sanitizeFileStem(userTitle);
    if (sanitized) return sanitized;
  }

  if (savedMoleculeName) {
    const sanitized = sanitizeFileStem(savedMoleculeName);
    if (sanitized) return sanitized;
  }

  if (formula) {
    const sanitized = sanitizeFileStem(normalizeFormulaForFileName(formula));
    if (sanitized) return sanitized;
  }

  if (isMarkush) return 'markush-drawing';
  return fallback ?? 'chem-drawing';
}

export interface UseExportFileStemParams {
  readonly document: DrawingDocument;
  readonly drawingTitle?: string | null;
  readonly moleculeId?: string | null;
  readonly currentResults?: SearchResults | null;
  readonly hasMarkushContent?: boolean;
}

// In-memory cache for moleculeId -> moleculeName lookups
const moleculeNameCache = new Map<string, string | null>();

/**
 * #1189: Hook that provides a reactive export file stem adhering to the user-title > molecule-name > formula rule.
 */
export function useExportFileStem({
  document,
  drawingTitle,
  moleculeId,
  currentResults,
  hasMarkushContent
}: UseExportFileStemParams): string {
  const userTitle = drawingTitle?.trim() || null;

  // Molecule name from structure search (identical match with enriched name)
  const searchMoleculeName =
    currentResults?.molecules?.find((m) => m.category === 'IDENTICAL' && m.name?.trim())?.name?.trim() || null;

  // Molecule name from saved molecule if moleculeId is present
  const [remoteMoleculeName, setRemoteMoleculeName] = useState<string | null>(null);
  const cachedMoleculeName = moleculeId ? (moleculeNameCache.get(moleculeId) ?? null) : null;

  useEffect(() => {
    if (!moleculeId || moleculeNameCache.has(moleculeId)) return;
    let active = true;
    getMoleculeSummary(moleculeId)
      .then((summary) => {
        const name = summary.name?.trim() || null;
        moleculeNameCache.set(moleculeId, name);
        if (active) setRemoteMoleculeName(name);
      })
      .catch(() => {
        moleculeNameCache.set(moleculeId, null);
        if (active) setRemoteMoleculeName(null);
      });
    return () => {
      active = false;
    };
  }, [moleculeId]);

  const moleculeName = searchMoleculeName || (moleculeId ? (cachedMoleculeName ?? remoteMoleculeName) : null);

  // Formula computation (synchronous Hill formula fallback + asynchronous RDKit formula)
  const hasNodes = document.structure.graph.nodes.length > 0 && !hasMarkushContent;
  let synchronousFormula: string | null = null;
  if (hasNodes) {
    try {
      synchronousFormula = queryMolecules(document)[0]?.formula ?? null;
    } catch {
      synchronousFormula = null;
    }
  }
  const [computedFormula, setComputedFormula] = useState<string | null>(null);

  useEffect(() => {
    if (!hasNodes) return;
    let active = true;
    computeMolecularFormula(toChemMoleculeV1({ ...document, derivedPropertySets: document.derivedPropertySets ?? [] }))
      .then((formula) => {
        if (active) setComputedFormula(formula);
      })
      .catch(() => {
        if (active) setComputedFormula(null);
      });
    return () => {
      active = false;
    };
  }, [document, hasNodes]);

  const effectiveFormula = hasNodes ? computedFormula || synchronousFormula : null;

  return resolveExportFileStem({
    userTitle,
    savedMoleculeName: moleculeName,
    formula: effectiveFormula,
    isMarkush: hasMarkushContent
  });
}
